import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { localDate } from 'app/events/helpers/date-fns-local';
import { isSameDayLocal } from 'app/events/helpers/is-same-day-local';
import { getVisibleDates } from 'app/events/helpers/month-visible-dates';
import { sortByStartDate } from 'app/events/helpers/sort-by-start-date';
import { addDays, addHours, areIntervalsOverlapping, differenceInCalendarDays, endOfWeek, format, isAfter, startOfWeek } from 'date-fns';
import { CalendarEventDetailsFragment } from 'generated/graphql';

@Component({
  selector: 'app-calendar-grid',
  templateUrl: './calendar-grid.component.html',
  styleUrls: ['./calendar-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // encapsulation: ViewEncapsulation.None
})
export class CalendarGridComponent implements OnChanges {
  @Input() events: CalendarEventDetailsFragment[] = [];
  @Input() focusDate = new Date();
  @Input() viewType: 'month' | 'week' = 'month';
  @Output() dayWithEventsClicked: EventEmitter<CalendarEventDetailsFragment[]> = new EventEmitter<CalendarEventDetailsFragment[]>();
  @Output() singleEventClicked: EventEmitter<CalendarEventDetailsFragment> = new EventEmitter<CalendarEventDetailsFragment>();

  today = localDate(new Date());
  weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  calendarDays = [];
  monthMaxEventsPerDay = 3;
  maxEventsPerDay = this.monthMaxEventsPerDay;

  // Display hours for week view
  displayHours = [];

  constructor(
    private _dialog: MatDialog,
    private _route: ActivatedRoute,
    private _router: Router
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.events || changes.focusDate) {
      this.processEvents(this.events);
    }
  }
  onDayClick(dayEvents: CalendarEventDetailsFragment[]) {
    this.dayWithEventsClicked.emit(dayEvents);
  }

  onSingleEventClick(event: CalendarEventDetailsFragment) {
    this.singleEventClicked.emit(event);
  }

  /** Key for calendar map */
  dayKey(date: Date) {
    return format(date, 'yyyy-MM-dd');
  }

  /** Get visible dates based on focus date and week/mnonth view */
  getVisibleDates() {
    const focusDate = localDate(this.focusDate);
    const month = this.focusDate.getMonth();
    let firstSunday: Date;
    let lastSaturday: Date;
    if (this.viewType === 'week') {
      firstSunday = startOfWeek(focusDate);
      lastSaturday = endOfWeek(focusDate);
    } else {
      const year = focusDate.getFullYear();
      const { firstSunday: monthFirstSunday, lastSaturday: monthLastSaturday } = getVisibleDates(year, month);
      firstSunday = monthFirstSunday;
      lastSaturday = monthLastSaturday;
    }
    return { firstSunday, lastSaturday, month };
  }

  /** Creates empty map of calendar dates to populate with events */
  createCalendarMap() {
    const calendarMap = {};
    const { firstSunday, lastSaturday, month } = this.getVisibleDates();
    let day = firstSunday;
    while (!isAfter(day, lastSaturday)) {
      calendarMap[this.dayKey(day)] =
      // Object for rendering calendar day
      {
        date: day,
        events: [],
        hourlyEvents: this.viewType === 'week' ? [] : null,
        isCurrentMonth: day.getMonth() === month,
        isToday: isSameDayLocal(day, this.today),
      };
      day = addDays(day, 1);
    }
    return calendarMap;
  }

  /** Populates displayHours based on earliest/latest */
  populateDisplayHours(earliestHour: number, latestHour: number) {
    const displayHours = [];
    for (let i = earliestHour; i <= latestHour; i++) {
      const date = new Date();
      date.setHours(i);
      displayHours.push(date);
    }
    this.displayHours = displayHours;
  }

  /**
   * Processes calendar event array and populates calendarDays with events.
   */
  processEvents(events: CalendarEventDetailsFragment[]) {
    const sortedEvents = events.sort(sortByStartDate);

    // Create map of calendar days
    let dayMap = this.createCalendarMap();

    // Track display hours & max all-day events in a day (for grid layout)
    let earliestHour = 24;
    let latestHour = 0;

    // Add events to calendar day map
    sortedEvents.forEach(event => {
      const startDate = localDate(event.startDate);
      if (!event.endDate || isSameDayLocal(startDate, localDate(event.endDate))) {
        // Starts & ends on same day
        const result = this.processSingleDayEvent(event, dayMap, earliestHour, latestHour);
        dayMap = result.eventMap;
        earliestHour = result.earliestHour;
        latestHour = result.latestHour;
        return;
      } else {
        // Event spans multiple days
        dayMap = this.processMultiDayEvent(event, dayMap);
      }
    });

    // For week view, calculate grid columns
    if (this.viewType === 'week') {
      Object.keys(dayMap).forEach(dayKey => {
        const day = dayMap[dayKey];
        dayMap[dayKey] = { ...day, gridColumns: this.getGridColumns(day) };
      });
    }

    // Turn map into day array
    this.calendarDays = Object.values(dayMap);

    // Set max events per day based on view type
    if (this.viewType === 'month') {
      this.maxEventsPerDay = this.monthMaxEventsPerDay;
    } else {
      this.maxEventsPerDay = Math.max(...this.calendarDays.map(day => day.events.length));
    }

    // Populate display hours
    this.populateDisplayHours(earliestHour, latestHour);
  }

  /**
   * Get number of grid columns required to display overlapping events
   * Example: if day has 2 events overlapping at one time and 3 events at another time, 6 columns are needed.
   *  */
  getGridColumns(day) {
    if (!day.hourlyEvents || day.hourlyEvents.length === 0) { return 1; }

    // Function to calculate the Greatest Common Divisor (GCD)
    function gcd(a, b) {
      while (b !== 0) {
        [a, b] = [b, a % b];
      }
      return a;
    }

    // Function to calculate the Least Common Multiple (LCM)
    function lcm(a, b) {
      return (a * b) / gcd(a, b);
    }

    // Calculate the LCM of all totalOverlapping values
    const totalOverlapping = day.hourlyEvents.reduce((acc, event) => lcm(acc, event.totalOverlapping), 1);

    return totalOverlapping;
  }

  /** Process event that starts & ends on the same day */
  processSingleDayEvent(event: CalendarEventDetailsFragment, eventMap, earliestHour, latestHour) {
    const startDate = localDate(event.startDate);
    const endDate = event.endDate ? localDate(event.endDate) : this.getDefaultEndDate(localDate(startDate));
    const dayKey = this.dayKey(startDate);
    if (this.viewType === 'month') {
      eventMap[dayKey]?.events.push(event);
    }
    if (this.viewType === 'week') {
      earliestHour = Math.min(earliestHour, startDate.getHours());
      // If ends at midnight, latest hour is 11pm
      latestHour = endDate.getHours() === 0 ? 23 : Math.max(latestHour, endDate.getHours());
      if (eventMap[dayKey]) {
        // Check if event overlaps with others on same day
        const eventsOnSameDay = eventMap[dayKey].hourlyEvents;
        const updatedHourlyEvents = [];
        let column = 0;
        let totalOverlapping = 1;

        eventsOnSameDay.forEach(e => {
          const updatedEvent = e;
          const startDate2 = localDate(e.startDate);
          const endDate2 = e.endDate ? localDate(e.endDate) : this.getDefaultEndDate(localDate(e.startDate));
          if (areIntervalsOverlapping(
            // Existing event interval
            {
              // Need to round to nearest 15 min for layout
              start: this.roundTimeToNearest15(startDate2), end: this.roundTimeToNearest15(endDate2)
            },
            // Current event interval
            { start: this.roundTimeToNearest15(startDate), end: this.roundTimeToNearest15(endDate) }
          )) {
            // Event overlaps, increment column
            column++;
            totalOverlapping++;
            updatedEvent.totalOverlapping = updatedEvent.totalOverlapping + 1;
          }
          updatedHourlyEvents.push(updatedEvent);
        });
        eventMap[dayKey].hourlyEvents = [
          ...updatedHourlyEvents,
          {
            ...event,
            column,
            totalOverlapping,
          }];
      }
    }
    return { eventMap, earliestHour, latestHour };
  }

  /** Process event that spans multiple days */
  processMultiDayEvent(event: CalendarEventDetailsFragment, eventMap) {
    const startDate = localDate(event.startDate);
    const eventEndDate = event.endDate ? localDate(event.endDate) : this.getDefaultEndDate(startDate);
    let recurrenceDate = localDate(startDate);
    recurrenceDate.setHours(0, 0, 0, 0);
    while (!isAfter(recurrenceDate, eventEndDate)) {
      const recurringDayKey = this.dayKey(recurrenceDate);
      // Event extends beyond today, add occurrence number
      if (eventEndDate && !isSameDayLocal(startDate, eventEndDate) && eventMap[recurringDayKey]) {
        eventMap[recurringDayKey]?.events.push({
          ...event,
          occurrenceNumber: differenceInCalendarDays(recurrenceDate, startDate) + 1,
          totalOccurrences: differenceInCalendarDays(eventEndDate, startDate) + 1,
        });
      }
      recurrenceDate = addDays(recurrenceDate, 1);
    }
    return eventMap;
  }

  /** If event doesn't have end date, show as one hour in week view (up until midnight) */
  getDefaultEndDate(date: Date) {
    const startDate = new Date(date);
    const endDate = addHours(startDate, 1);
    // If one hour pushes past midnight, set end date to midnight to keep on same day
    if (endDate.getDate() !== startDate.getDate()) {
      endDate.setHours(0, 0, 0, 0);
    }
    return endDate;
  };

  /** Round time to 15 minutes when calculating overlapping events for layout */
  roundTimeToNearest15(date: Date) {
    const roundedDate = localDate(date);
    roundedDate.setMinutes(Math.round(roundedDate.getMinutes() / 15) * 15);
    return roundedDate;
  }

  getGridRow(event) {
    const displayHourStart = this.displayHours[0].getHours();
    const start = localDate(event.startDate);
    const end = event.endDate ? (localDate(event.endDate)) : this.getDefaultEndDate(start);
    const startHour = start.getHours();
    let endHour = end.getHours();
    const startMinute = start.getMinutes();
    let endMinute = end.getMinutes();
    if (endHour === 0) {
      endHour = 24;
      endMinute = 0;
    }
    const startRow = Math.round((startHour - displayHourStart) * 4 + startMinute / 15);
    const endRow = Math.round((endHour - displayHourStart) * 4 + endMinute / 15);
    return `${startRow + 1} / ${endRow + 1}`;
  }

  getGridColumn(event, day) {
    if (!day.hourlyEvents) { return 1; }
    const overlaps = day.gridColumns / event.totalOverlapping;
    const colStart = overlaps * event.column + 1;
    const colEnd = colStart + overlaps;
    return `${colStart} / ${colEnd}`;
  }

  isSingleLine(event) {
    const start = localDate(event.startDate);
    const end = event.endDate ? (localDate(event.endDate)) : this.getDefaultEndDate(start);
    const durationMinutes = (end.getTime() - start.getTime()) / 60000;
    return durationMinutes <= 15;
  }

  isDoubleLine(event) {
    const start = localDate(event.startDate);
    const end = event.endDate ? (localDate(event.endDate)) : this.getDefaultEndDate(start);
    const durationMinutes = (end.getTime() - start.getTime()) / 60000;
    return durationMinutes <= 30 && durationMinutes > 15;
  }
}
